// snp_export.js - Touchstone (SnP) File Export for S-Parameters
import { computeSParamsDiffAuto,
         isSelfReferenced, sparamsForPoint, usableSweepPoints } from './sparameters.js';

// Map transmission line types to short names
const TL_TYPE_SHORT_NAMES = {
    'microstrip': 'ms',
    'diff_microstrip': 'diff_ms',
    'stripline': 'sl',
    'diff_stripline': 'diff_sl',
    'gcpw': 'gcpw',
    'diff_gcpw': 'diff_gcpw',
    'broadside_stripline': 'bs_sl',
    'coax': 'coax',
    'rect_waveguide': 'rect_wg'
};

/**
 * Format length with appropriate unit
 * @param {number} length - Length in meters
 * @returns {{value: number, unit: string}} - Formatted value and unit
 */
function formatLength(length) {
    if (length >= 1) {
        return { value: length, unit: 'm' };
    } else if (length >= 0.001) {
        return { value: length * 1000, unit: 'mm' };
    } else {
        return { value: length * 1e6, unit: 'um' };
    }
}

/**
 * Generate filename for Touchstone file
 * @param {string} tlType - Transmission line type
 * @param {number} length - Line length in meters
 * @param {boolean} isDifferential - True for 4-port
 * @returns {string} - Filename
 */
function generateFilename(tlType, length, isDifferential) {
    const shortName = TL_TYPE_SHORT_NAMES[tlType] || tlType;
    const { value, unit } = formatLength(length);
    const ext = isDifferential ? 's4p' : 's2p';
    // Format value to remove trailing zeros
    const valueStr = value.toFixed(3).replace(/\.?0+$/, '');
    return `${shortName}-${valueStr}${unit}.${ext}`;
}

/**
 * Generate parameter comments for Touchstone file
 * @param {object} params - Simulation parameters
 * @returns {Array<string>} - Comment lines
 */
function generateParamComments(params) {
    const lines = [];
    lines.push('! Touchstone file generated by 2D Field Solver');
    lines.push('! https://hforsten.com/field_solver.html');
    lines.push('!');
    lines.push('! Transmission Line Parameters:');
    lines.push(`!   Type: ${params.tlType}`);
    // Line types whose geometry is not a trace-on-substrate stackup supply their own
    // description instead of the trace-width/thickness/substrate-height trio.
    if (params.geometryLines) {
        for (const l of params.geometryLines) lines.push(l);
    } else {
        lines.push(`!   Trace width: ${(params.traceWidth * 1e6).toFixed(1)} um`);
        lines.push(`!   Trace thickness: ${(params.traceThickness * 1e6).toFixed(2)} um`);
        lines.push(`!   Substrate height: ${(params.substrateHeight * 1e6).toFixed(1)} um`);
        lines.push(`!   Substrate permittivity: ${params.epsilonR}`);
        lines.push(`!   Loss tangent: ${params.tanDelta}`);
        lines.push(`!   Conductivity: ${params.sigma.toExponential(2)} S/m`);
    }
    if (params.traceSpacing) {
        lines.push(`!   Trace spacing: ${(params.traceSpacing * 1e6).toFixed(1)} um`);
    }
    if (params.surfaceRoughness > 0) {
        lines.push(`!   Surface roughness RMS: ${(params.surfaceRoughness * 1e6).toFixed(2)} um`);
    }
    if (params.plating) {
        lines.push(`!   Surface plating: enabled`);
        lines.push(`!     Conductivity: ${(params.plating.sigma * 1e-6).toFixed(1)} MS/m`);
        lines.push(`!     Thickness: ${(params.plating.thickness * 1e6).toFixed(2)} um`);
        lines.push(`!     Roughness RMS: ${(params.plating.rq * 1e6).toFixed(2)} um`);
        if (params.plating.rq_interface !== undefined && params.plating.rq_interface !== params.plating.rq) {
            lines.push(`!     Interface roughness RMS: ${(params.plating.rq_interface * 1e6).toFixed(2)} um`);
        }
        // A curved or self-bounded conductor has ONE continuous surface, so the
        // top/sides/bottom selection has nothing to select between (the solvers
        // normalize it to `all`) and listing the three faces would be misleading.
        // Coax has two such surfaces, and selects between THEM instead.
        const surfaces = [];
        if (params.plating.inner !== undefined || params.plating.outer !== undefined) {
            if (params.plating.inner) surfaces.push('the inner conductor');
            if (params.plating.outer) surfaces.push('the shield');
            lines.push(`!     Applied to: ${surfaces.join(' and ') || 'nothing (bare metal)'}`);
        } else {
            if (params.plating.top) surfaces.push('top');
            if (params.plating.sides) surfaces.push('sides');
            if (params.plating.bottom) surfaces.push('bottom');
            lines.push(`!     Applied to: ${params.plating.all ? 'the whole surface' : surfaces.join(', ')}`);
        }
    }
    lines.push('!');
    lines.push('! S-Parameter Settings:');
    lines.push(`!   Line length: ${(params.length * 1000).toFixed(3)} mm`);
    // zRef === null for a self-referenced medium: generateS2P states the real
    // (frequency-dependent) reference itself, and printing a nominal number here too
    // would put two contradicting "Reference impedance" lines in one header.
    if (params.zRef !== null && params.zRef !== undefined) {
        lines.push(`!   Reference impedance: ${params.zRef} Ohm`);
    }
    lines.push(`!   Frequency range: ${(params.freqStart / 1e9).toFixed(3)} - ${(params.freqStop / 1e9).toFixed(3)} GHz`);
    lines.push(`!   Number of points: ${params.numPoints}`);
    lines.push('!');
    return lines;
}

// One S-parameter as Touchstone magnitude and angle (degrees).
function magAngle(s) {
    return `${s.abs().toExponential(6)}  ${(s.arg() * 180 / Math.PI).toFixed(2)}`;
}

// Header without the parameter comments: the title, extra description lines, the line
// length and (when showRef) the reference impedance.
function bareHeader(length, Z_ref, showRef, extra = []) {
    return ['! Touchstone file generated by 2D Field Solver', ...extra, `! Line length: ${length * 1000} mm`,
        ...(showRef ? [`! Reference impedance: ${Z_ref} Ohm`] : [])];
}

// Comment header: the parameter comments when there are parameters, the bare header
// otherwise, followed by the tail lines.
function headerComments(params, comments, bare, tail) {
    const head = params && Object.keys(params).length > 0 ? generateParamComments(comments) : bare;
    return [...head, ...tail];
}

/**
 * Generate Touchstone file content for 2-port (s2p) network
 *
 * @param {Array} frequencySweepResults - Array of {freq, result} objects
 * @param {number} length - Line length in meters
 * @param {number} Z_ref - Reference impedance in Ohms
 * @param {object} params - Additional parameters for comments
 * @returns {string} - Touchstone file content
 */
function generateS2P(frequencySweepResults, length, Z_ref, params = {}) {
    const lines = [];

    // A self-referenced medium (waveguide) normalizes each point to its own modal
    // impedance and cannot represent its below-cutoff points at all — see
    // usableSweepPoints. Drop them and say so in the header rather than writing rows a
    // consumer would read as a lossy through.
    const selfRef = isSelfReferenced(frequencySweepResults);
    const points = usableSweepPoints(frequencySweepResults);
    const dropped = frequencySweepResults.length - points.length;
    if (!points.length) {
        throw new Error(selfRef
            ? 'Every sweep point is below the waveguide cutoff — there are no propagating ' +
              'S-parameters to export. Raise the sweep frequency above the cutoff.'
            : 'No sweep points to export.');
    }

    // Comment header with parameters. The caller's freqStart/freqStop/numPoints describe
    // the SWEEP; restate them from `points` so they describe the rows actually written
    // (identical unless a self-referenced medium dropped its below-cutoff points).
    lines.push(...headerComments(params, {
        ...params, length,
        zRef: selfRef ? null : Z_ref,
        freqStart: points[0].freq,
        freqStop: points[points.length - 1].freq,
        numPoints: points.length,
    }, [...bareHeader(length, Z_ref, !selfRef), '!'], []));
    if (selfRef) {
        // Touchstone carries a single real reference impedance, so a frequency-dependent
        // modal reference cannot be stated in the option line. The R value below is
        // nominal. The matrix itself is that of a matched section of guide and cascades
        // correctly with other guide parts.
        lines.push('! Reference impedance: the modal impedance Z0(f) of the guided mode,');
        lines.push('! which is frequency-dependent. S11 = S22 = 0, and the');
        lines.push('! R value on the option line below is nominal only.');
        const fc = points[0].result.modes[0].fc;
        if (dropped > 0 && isFinite(fc)) {
            lines.push(`! ${dropped} sweep point(s) below the ${(fc / 1e9).toFixed(3)} GHz cutoff are omitted`);
            lines.push('! (the mode is evanescent there, with an imaginary modal impedance).');
        }
        lines.push('!');
    }

    // Option line: Hz S MA R 50 (Magnitude/Angle format)
    lines.push(`# Hz S MA R ${Z_ref}`);
    lines.push('! Freq(Hz)      S11_mag    S11_ang    S21_mag    S21_ang    S12_mag    S12_ang    S22_mag    S22_ang');

    // Data lines
    for (const { freq, result } of points) {
        const sp = sparamsForPoint(freq, result, length, Z_ref);
        lines.push(`${freq.toExponential(6)}  ${[sp.S11, sp.S21, sp.S12, sp.S22].map(magAngle).join('  ')}`);
    }

    return lines.join('\n');
}

/**
 * Generate Touchstone file content for 4-port (s4p) differential network
 *
 * Port assignment: 1=in+, 2=out+, 3=in-, 4=out-
 *
 * @param {Array} frequencySweepResults - Array of {freq, result} objects
 * @param {number} length - Line length in meters
 * @param {number} Z_ref - Reference impedance in Ohms
 * @param {object} params - Additional parameters for comments
 * @returns {string} - Touchstone file content
 */
function generateS4P(frequencySweepResults, length, Z_ref, params = {}) {
    const lines = [];

    // Comment header with parameters
    lines.push(...headerComments(params, { ...params, length, zRef: Z_ref },
        bareHeader(length, Z_ref, true, ['! Differential transmission line']),
        ['! Port assignment: 1=in+, 2=in-, 3=out+, 4=out-', '!']));

    // Option line: Hz S MA R 50 (Magnitude/Angle format)
    lines.push(`# Hz S MA R ${Z_ref}`);
    lines.push('! Freq(Hz)');
    lines.push('! S11 S12 S13 S14 / S21 S22 S23 S24 / S31 S32 S33 S34 / S41 S42 S43 S44');

    // Data lines
    for (const { freq, result } of frequencySweepResults) {
        const oddMode = result.modes.find(m => m.mode === 'odd');
        const evenMode = result.modes.find(m => m.mode === 'even');

        const sp = computeSParamsDiffAuto(freq, oddMode.RLGC, evenMode.RLGC, result.physMatrix, length, Z_ref);
        const S = sp.S;

        // The frequency and the first row (S11-S14), then rows 2-4 indented as continuations
        for (let row = 0; row < 4; row++) {
            const lead = row === 0 ? freq.toExponential(6) : '                ';
            lines.push(`${lead}  ${S[row].map(magAngle).join('  ')}`);
        }
    }

    return lines.join('\n');
}

/**
 * Download a file with the given content
 *
 * @param {string} content - File content
 * @param {string} filename - Name of the file to download
 * @param {string} type - MIME type
 */
function downloadFile(content, filename, type = 'text/plain') {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Export S-parameters to Touchstone file
 *
 * @param {Array} frequencySweepResults - Array of {freq, result} objects
 * @param {number} length - Line length in meters
 * @param {number} Z_ref - Reference impedance in Ohms
 * @param {boolean} isDifferential - True for 4-port differential, false for 2-port
 * @param {object} params - Simulation parameters for comments and filename
 */
function exportSnP(frequencySweepResults, length, Z_ref, isDifferential, params = {}) {
    let content, filename;

    const tlType = params.tlType || 'microstrip';
    filename = generateFilename(tlType, length, isDifferential);

    if (isDifferential) {
        content = generateS4P(frequencySweepResults, length, Z_ref, params);
    } else {
        content = generateS2P(frequencySweepResults, length, Z_ref, params);
    }

    downloadFile(content, filename);
    return filename;
}

export { exportSnP, generateS2P, generateS4P, generateFilename, downloadFile };
